import { Component, Input, Output, EventEmitter, ViewChildren, AfterViewChecked } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { TextBoxContainerComponent } from '@progress/kendo-angular-inputs';
import { FileRestrictions, SelectEvent, ClearEvent, RemoveEvent, FileInfo, UploadComponent, FileState } from '@progress/kendo-angular-upload';
import { FormDialogTemplate } from './form-dialog.template';
import { CgpSnackbarService } from '../cgp-snackbar/cgp-snackbar.service';
import { CgpConfirmationDialogService } from '../cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { FormControl, FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'form-dialog',
    templateUrl: './form-dialog.component.html',
    styleUrls: ['./form-dialog.component.scss'],
    host: { 'class': 'dialog-window' },
})
export class FormDialogComponent implements AfterViewChecked {
    constructor(
        private readonly cgpConfirmationDialogService: CgpConfirmationDialogService,
        private readonly cgpSnackbarService: CgpSnackbarService,
        private readonly formBuilder: FormBuilder,
    ) {
        this.customForm = this.formBuilder.group({ customControl: [] });
    }

    @ViewChildren('input') vc;
    ngAfterViewChecked(): void {
        if (this.canShow()) this.focusFirst();
    }

    @Input()
    set template(obj: FormDialogTemplate) {
        this.dialogTemplate = obj;
    }

    @Output()
    cancel: EventEmitter<any> = new EventEmitter();

    @Output()
    save: EventEmitter<any> = new EventEmitter();

    initialized: boolean;
    actionFormDialog: any = undefined;
    panelBarControlSelectedItem: any = undefined;
    isActionFormDialogNew = false;
    commentFile: any;
    customForm: FormGroup;
    dialogTemplate: FormDialogTemplate;
    uploadImgClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    focusFirst() {
        if (this.initialized) return;
        try {
            try {
                this.vc.first.nativeElement.focus();
            } catch (e) { this.vc.first.focus() }
        } catch (e) { }
        finally { this.initialized = true }
    }

    isShowControl = (control: any, check: string = undefined, editForm: any = undefined): boolean => {
        try {
            const form: any = editForm ? editForm : this.dialogTemplate.editForm;
            return (check ? control.type === check : true) &&
                form.controls[control.name] !== undefined;
        } catch (e) {
            return false;
        }
    }


    getTitle = (): string => {
        let title: string;
        if (this.dialogTemplate.customTitle != null) {
            title = `${this.dialogTemplate.customTitle} `;
        }
        else {
            title = this.dialogTemplate.isNew ? 'Add New ' : 'Edit ';
        }

        try {
            if (typeof this.dialogTemplate.title === 'string') {
                title += this.dialogTemplate.title
            } else if (this.dialogTemplate.title && this.dialogTemplate.title.value) {
                title = this.dialogTemplate.title.value;
            }
        } catch (e) { }
        return title;
    }


    getControlClass = (control: any, form: any) => {
        const ctrl = this.dialogTemplate.editForm.get(control.name);
        if (this.isShowControl(control, null, form)) {
            let formClass = '';
            if (control.stretch) formClass += 'stretch ';
            if (!control.stretch && this.dialogTemplate.largeForm) formClass += 'flex-column ';
            if (control.type !== "radiogroup" && control.type !== "grid") formClass += (this.dialogTemplate.inline || control.type === 'boolean') ? 'k-form-inline ' : 'k-form ';
            if (ctrl && ctrl.disabled) formClass += 'disabled';
            return formClass;
        }
        return 'hidden';
    }

    getLabelClass = (obj: any): string => {
        let response = 'cgp-blue';
        if (obj) {
            switch (obj.flag) {
                case 'error':
                case 'warning':
                case 'danger':
                    response = 'cgp-red info-text';
                    break;
                case 'info':
                    response = 'secondary-blue info-text';
                    break;
            }
        }
        return response;
    }

    private readonly getTextClass = (prefix: string, control: any): string => {
        const ctrl = this.dialogTemplate.editForm.controls[control.name];
        const valid = ctrl.valid ? ' ng-valid' : ' ng-invalid';
        const touched = ctrl.touched ? ' ng-touched' : ' ng-untouched';
        const value = ctrl.value ? ' ng-not-empty' : '';
        return `${prefix}${valid}${touched}${value}`;
    }

    getInputClass = (control: any) => {
        return this.getTextClass('modelo-text-input', control);
    }

    getTextAreaClass = (control: any) => {
        return this.getTextClass('textarea', control);
    }

    canShow = (): boolean => {
        return this.dialogTemplate && this.dialogTemplate.value;
    }


    handleFilter = (e: any, control: any) => {
        const currentValue = this.dialogTemplate.editForm.value[control.name]
        if (control.service && control.service.handleFilter && control.filterable === undefined? true : control.filterable) {
            control.service.handleFilter(e, control.textField, currentValue);
        }
    }

    handleTabSelect = (e) => {
        this.dialogTemplate.selectTab(e.index);
    }

    handleGridControlCancel = ({ sender, rowIndex }) => sender.closeRow(rowIndex);

    isRequired = (c: any): boolean => {
        const ctrl = this.dialogTemplate.editForm.get(c.name);
        return ctrl.invalid;
    }

    private isStateValid(state: string): boolean {
        const _state: string = state ? state : "";
        switch (_state.toLowerCase()) {
            case 'update':
            case 'edit':
                return !this.dialogTemplate.isNew;
            case 'add':
            case 'create':
            case 'new':
                return this.dialogTemplate.isNew;
            default:
                return true;
        }
    }

    isShowFooterLabel = (): boolean => {
        return this.dialogTemplate.footerLabel;
    }

    isShowNotificationLabel = (): boolean => {
        const c = this.dialogTemplate.notificationLabel;
        return c && this.isStateValid(c.state);
    }

    getCustomForm(file: any, name: string): FormGroup {
        if (!file.form) file.form = this.formBuilder.group({ comment: ['', Validators.required] });
        return file.form
    }


    onUploadComment = (control: any, file: any) => {
        if (control.comment) {
            file[0].form.reset(file);
        }
    }


    onCommentSave = (control: any, files: any[], form: FormGroup, c: UploadComponent) => {
        files[0].comment = files[0].form.get("comment").value;
        delete files[0].form;
        if (control.save) {
            control.save(files, form, c);
            files[0].state = FileState.Initial;
        }
    }


    onCommentCancel = (file: FileInfo, c: UploadComponent) => c.fileList.remove(file.uid);

    getImgSrc = (fileName: string) => {
        return this.dialogTemplate.imagePreviews.find(x => x.name === fileName);
    }

    openUploadImg = () => {
        this.uploadImgClick.next(true);
    }

    isUploadImgOpen = () => {
        return this.uploadImgClick.value;
    }

    closeUploadImg = () => {
        this.uploadImgClick.next(false);
    }

    private onSave(e, isSubmit = false): void {
        e.preventDefault();

        if (isSubmit) {

            this.cgpConfirmationDialogService.showConfirmationDialog({
                title: 'Submit Confirmation',
                message: 'Are you sure that you want to submit this request?',
                alert: 'warning',
                onYesClick: () => {
                    this.handleSave(true)
                    this.cgpConfirmationDialogService.closeConfirmationDialog();
                },
                onNoClick: () => {
                    this.cgpConfirmationDialogService.closeConfirmationDialog();
                }
            });
        } else {
            this.handleSave();
        }
    }

    private handleSave(isSubmit = false) {

        this.dialogTemplate.next(null);
        this.dialogTemplate.editForm.enable();

        if (this.dialogTemplate.save) {
            this.dialogTemplate.save({ dataItem: this.dialogTemplate.editForm.value, isNew: this.dialogTemplate.isNew, isSubmit });
        }

        this.save.emit({ dataItem: this.dialogTemplate.editForm.value, isNew: this.dialogTemplate.isNew, isSubmit });
    }


    private onCancel(e): void {
        e.preventDefault();
        this.closeForm();
    }


    private closeForm(): void {
        this.dialogTemplate.next(null);
        this.dialogTemplate.imagePreviews = [];

        if (this.dialogTemplate.cancel) {
            this.dialogTemplate.cancel();
        }
        this.cancel.emit();
    }


    private isDisabled(state: string): boolean {
        return state === 'always' ||
            state === 'create' && this.dialogTemplate.isNew ||
            state === 'update' && !this.dialogTemplate.isNew;
    }


    private onPanelBarItemEdit(control, isNew) {
        if (control) {
            this.isActionFormDialogNew = isNew;
            this.actionFormDialog = control.editTemplate.formDialog;
            this.actionFormDialog.control = control;

            const dataItem = isNew ? undefined : this.panelBarControlSelectedItem;
            this.actionFormDialog.edit(dataItem);

        }
    }


    private actionFormSaveHandler(editDataItem) {
        if (!this.isActionFormDialogNew) editDataItem.id = this.panelBarControlSelectedItem.id;
        this.actionFormDialog.save(editDataItem, this.dialogTemplate.editForm, this.isActionFormDialogNew);
        this.isActionFormDialogNew = false;
    }


    private actionFormCancelHandler() {
        this.actionFormDialog = undefined;
        this.isActionFormDialogNew = false;
    }


    private panelBarControlstateChange(dataItems: any[]) {
        this.panelBarControlSelectedItem = dataItems.find(item => item.selected);
    }


    private onPanelBarItemDelete(control, dataItem) {
        this.cgpConfirmationDialogService.showConfirmationDialog({
            title: 'Delete Confirmation',
            message: 'Are you sure that you want to delete this record?',
            alert: 'danger',
            onYesClick: () => {
                control.editTemplate.formDialog.delete(dataItem);
                this.cgpConfirmationDialogService.closeConfirmationDialog();
            },
            onNoClick: () => {
                this.cgpConfirmationDialogService.closeConfirmationDialog();
            }
        });
    }

    private onDialogAction(control: any) {
        control.callback()
            .then(response => {
                if (control.close) this.closeForm();
            });
    }




}
